<template>
    <div class="tab-detail">
        <div class="header">
            <div class="title">{{ $t("googlePlay.apps") }}</div>
            <div class="header-actions">
                <a-button type="primary" @click="showAddModal = true">
                    <span class="material-icons" style="font-size: 16px; margin-right: 4px;">add</span>
                    {{ $t("googlePlay.addApp") }}
                </a-button>
                <a-button @click="fetchApps" :loading="loading">
                    <a-icon type="reload" /> {{ $t("googlePlay.refresh") }}
                </a-button>
            </div>
        </div>
        <div class="apps-container" v-if="!loading">
            <div class="app-card" v-for="app in apps" :key="app._id">
                <div class="app-icon">
                    <img :src="app.iconUrl" v-if="app.iconUrl" />
                    <span class="material-icons" v-else>android</span>
                </div>
                <div class="info">
                    <h3 class="app-name">{{ app.appName }}</h3>
                    <div class="package-name">{{ app.packageName }}</div>
                    <div class="meta">
                        <span v-if="app.totalReviewCount">
                            <span class="material-icons">reviews</span> {{ app.totalReviewCount }} reviews
                        </span>
                        <span v-if="app.lastSyncedAt">
                            <span class="material-icons">sync</span> {{ formatDate(app.lastSyncedAt) }}
                        </span>
                    </div>
                </div>
                <div class="actions">
                    <a-button size="small" @click="syncReviews(app)" :loading="syncingApp === app._id">
                        <a-icon type="sync" /> {{ $t("googlePlay.syncReviews") }}
                    </a-button>
                    <a-button size="small" type="danger" @click="confirmRemove(app)">
                        <a-icon type="delete" />
                    </a-button>
                </div>
            </div>
        </div>
        <div class="loading" v-else>
            <a-spin />
        </div>
        <div class="empty" v-if="!loading && apps.length === 0">
            <a-empty :description="$t('googlePlay.noAppsFound')">
                <a-button type="primary" @click="showAddModal = true">
                    {{ $t("googlePlay.addApp") }}
                </a-button>
            </a-empty>
        </div>

        <!-- Add App Modal -->
        <a-modal
            v-model="showAddModal"
            :title="$t('googlePlay.addApp')"
            @ok="addApp"
            :confirm-loading="adding"
        >
            <a-form-item :label="$t('googlePlay.packageName')" :required="true">
                <a-input
                    v-model="newApp.packageName"
                    placeholder="com.example.myapp"
                />
            </a-form-item>
            <a-form-item :label="$t('googlePlay.appName')" :required="true">
                <a-input
                    v-model="newApp.appName"
                    :placeholder="$t('googlePlay.appNamePlaceholder')"
                />
            </a-form-item>
            <a-form-item :label="$t('googlePlay.iconUrl')">
                <a-input
                    v-model="newApp.iconUrl"
                    placeholder="https://..."
                />
            </a-form-item>
        </a-modal>

        <!-- Remove Confirmation Modal -->
        <a-modal
            v-model="removeModal.visible"
            :title="$t('googlePlay.removeApp')"
            @ok="removeApp"
            :ok-type="'danger'"
        >
            <p>{{ $t("googlePlay.removeAppConfirm", { name: removeModal.app ? removeModal.app.appName : '' }) }}</p>
        </a-modal>
    </div>
</template>

<script>
import { GOOGLE_PLAY_API } from "../../../../../api/google-play";
import moment from "moment";

export default {
    name: "AppList",
    props: {
        channel: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            apps: [],
            loading: false,
            showAddModal: false,
            adding: false,
            syncingApp: null,
            newApp: {
                packageName: "",
                appName: "",
                iconUrl: "",
            },
            removeModal: {
                visible: false,
                app: null,
            },
        };
    },
    methods: {
        async fetchApps() {
            this.loading = true;
            try {
                const response = await GOOGLE_PLAY_API.listApps(this.channel._id);
                this.apps = response.data;
            } catch (err) {
                this.$message.error(this.$t("googlePlay.errorFetchingApps"));
            } finally {
                this.loading = false;
            }
        },
        async addApp() {
            if (!this.newApp.packageName || !this.newApp.appName) {
                this.$message.warning(this.$t("googlePlay.fillRequiredFields"));
                return;
            }
            this.adding = true;
            try {
                await GOOGLE_PLAY_API.addApp(this.channel._id, this.newApp);
                this.$message.success(this.$t("googlePlay.appAdded"));
                this.showAddModal = false;
                this.newApp = { packageName: "", appName: "", iconUrl: "" };
                await this.fetchApps();
            } catch (err) {
                var errorMessage = err.response && err.response.data && err.response.data.message;
                this.$message.error(errorMessage || this.$t("googlePlay.errorAddingApp"));
            } finally {
                this.adding = false;
            }
        },
        async syncReviews(app) {
            this.syncingApp = app._id;
            try {
                await GOOGLE_PLAY_API.fetchReviews(this.channel._id, app.packageName);
                this.$message.success(this.$t("googlePlay.reviewsSynced"));
                await this.fetchApps();
            } catch (err) {
                this.$message.error(this.$t("googlePlay.errorSyncingReviews"));
            } finally {
                this.syncingApp = null;
            }
        },
        confirmRemove(app) {
            this.removeModal.app = app;
            this.removeModal.visible = true;
        },
        async removeApp() {
            try {
                await GOOGLE_PLAY_API.removeApp(this.removeModal.app._id);
                this.$message.success(this.$t("googlePlay.appRemoved"));
                this.removeModal.visible = false;
                this.removeModal.app = null;
                await this.fetchApps();
            } catch (err) {
                this.$message.error(this.$t("googlePlay.errorRemovingApp"));
            }
        },
        formatDate(date) {
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
    },
    mounted() {
        this.fetchApps();
    },
};
</script>

<style lang="scss" scoped>
.tab-detail {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 20px;

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .title {
            font-size: 18px;
            font-weight: 700;
            color: var(--color-text-strong-default);
        }

        .header-actions {
            display: flex;
            gap: 10px;
        }
    }

    .apps-container {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
        gap: 24px;
        margin-top: 15px;

        .app-card {
            background: var(--color-bg-subtle);
            border-radius: 12px;
            padding: 20px;
            display: flex;
            gap: 16px;
            transition: transform 0.2s, box-shadow 0.2s;

            &:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
            }

            .app-icon {
                width: 60px;
                height: 60px;
                background: #34a853;
                border-radius: 12px;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                overflow: hidden;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .material-icons {
                    font-size: 28px;
                    color: #fff;
                }
            }

            .info {
                flex: 1;
                min-width: 0;

                .app-name {
                    margin: 0 0 4px;
                    font-size: 16px;
                    font-weight: 600;
                    color: var(--color-text-strong-default);
                }

                .package-name {
                    font-size: 12px;
                    color: var(--color-text-default);
                    font-family: monospace;
                    margin-bottom: 8px;
                }

                .meta {
                    display: flex;
                    gap: 16px;
                    flex-wrap: wrap;
                    color: var(--color-text-default);
                    font-size: 12px;

                    span {
                        display: flex;
                        align-items: center;
                        gap: 4px;

                        .material-icons {
                            font-size: 14px;
                        }
                    }
                }
            }

            .actions {
                display: flex;
                flex-direction: column;
                gap: 8px;
            }
        }
    }

    .loading,
    .empty {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 300px;
    }
}
</style>
